import {
    createFileRoute,
    Outlet,
    redirect,
    useRouter,
    useRouterState,
} from '@tanstack/react-router';
import { useEffect } from 'react';

import { AppSessionLoading } from '@apps/web/components/app_session_loading';
import { SettingsUnsavedChangesProvider } from '@apps/web/components/settings/unsaved_settings_guard';
import { useSessionState } from '@apps/web/context/session_state_provider';
import { AttachmentViewerProvider } from '@apps/web/features/phoebe_chat_v3/ui/attachment_viewer';
import { useDeepLinkOrganization } from '@apps/web/hooks/use_deep_link_organization';
import { ModeOverrideProvider } from '@apps/web/lib/mode_store';
import { sanitizeReturnToPath } from '@apps/web/lib/post_login_redirect';
import { type Mode } from '@libraries/typescript/api_client';

function isPublicExtensionPath(pathname: string): boolean {
    return /^\/extension(?:-local)?\/?$/.test(pathname);
}

function returnToPathForCurrentOrigin(href: string): string {
    return sanitizeReturnToPath(href, window.location.origin) ?? '/';
}

// Read `organizationId` / `mode` / `source` off the URL without declaring a
// root-level `validateSearch`, which would narrow every child route's search
// type and break pages whose own schemas add fields beyond these. A link like
// `/foo?organizationId=<uuid>` still pins the tab to the right org regardless
// of which route it lands on, via `useDeepLinkOrganization` below.
function readDeepLinkOrganization(search: unknown): {
    readonly organizationId: string | undefined;
    readonly mode: Mode | undefined;
    readonly source: string | undefined;
} {
    if (typeof search !== 'object' || search === null) {
        return {
            organizationId: undefined,
            mode: undefined,
            source: undefined,
        };
    }
    const record = search as Record<string, unknown>;
    const organizationIdRaw = record['organizationId'];
    const modeRaw = record['mode'];
    const sourceRaw = record['source'];
    return {
        organizationId:
            typeof organizationIdRaw === 'string'
                ? organizationIdRaw
                : undefined,
        mode: modeRaw === 'live' || modeRaw === 'sandbox' ? modeRaw : undefined,
        source: typeof sourceRaw === 'string' ? sourceRaw : undefined,
    };
}

export const Route = createFileRoute('/_app')({
    beforeLoad: async ({ context, location }) => {
        const { sessionState } = context;
        if (
            !sessionState?.session &&
            !isPublicExtensionPath(location.pathname)
        ) {
            throw redirect({
                to: '/sign-in',
                search: {
                    return_to: returnToPathForCurrentOrigin(location.href),
                },
            });
        }
    },
    component: RouteComponent,
});

function RouteComponent() {
    const { session, initialized } = useSessionState();
    const router = useRouter();
    const pathname = router.state.location.pathname;

    useEffect(() => {
        if (!initialized) {
            return;
        }
        if (!session && !isPublicExtensionPath(pathname)) {
            void router.navigate({
                to: '/sign-in',
                search: {
                    return_to: returnToPathForCurrentOrigin(
                        router.state.location.href,
                    ),
                },
                replace: true,
            });
        }
    }, [initialized, pathname, router, session]);

    // Keep a loading screen up while Ory boots and while we navigate a
    // logged-out user to sign-in, so protected routes never paint a blank page.
    if (!session && !isPublicExtensionPath(pathname)) {
        return <AppSessionLoading />;
    }

    if (!session) {
        return <Outlet />;
    }

    return <AuthenticatedRouteOutlet />;
}

function AuthenticatedRouteOutlet() {
    // `state.location` is the pending location; reading it lifts the pin while
    // the old page is still mounted. `resolvedLocation` is undefined only
    // before the first load commits.
    const search = useRouterState({
        select: (state) => (state.resolvedLocation ?? state.location).search,
    });
    const {
        organizationId: deepLinkOrganizationId,
        mode: deepLinkMode,
        source,
    } = readDeepLinkOrganization(search);

    // Pin mode for a deep-link tab so the page is born in the right mode (live
    // for extension opens) with no sandbox→live flash and no auto-switch toast.
    // Context-only + per-tab: the global mode store is untouched (other tabs
    // unaffected), and the pin lifts once the user navigates off the deep-link
    // URL. Extension opens always operate on live data; honor an explicit
    // `?mode=` when present. The provider is always rendered (`null` when there
    // is no pin) because adding or dropping the element remounts the whole app.
    const deepLinkOverrideMode: Mode | null =
        deepLinkMode ?? (source === 'extension' ? 'live' : null);

    return (
        <ModeOverrideProvider mode={deepLinkOverrideMode}>
            <SettingsUnsavedChangesProvider>
                <AttachmentViewerProvider>
                    <DeepLinkOrganizationGate
                        deepLinkOrganizationId={deepLinkOrganizationId}
                        deepLinkMode={deepLinkMode}
                    />
                </AttachmentViewerProvider>
            </SettingsUnsavedChangesProvider>
        </ModeOverrideProvider>
    );
}

function DeepLinkOrganizationGate({
    deepLinkOrganizationId,
    deepLinkMode,
}: {
    readonly deepLinkOrganizationId: string | undefined;
    readonly deepLinkMode: Mode | undefined;
}) {
    // Reads `useMode()` under the override above, so for a hard-pinned deep-link
    // tab the org/mode already match and this hook neither switches nor toasts —
    // it only gates while a genuine in-tab switch is still settling.
    const { isWaitingForDeepLinkOrganization } = useDeepLinkOrganization(
        deepLinkOrganizationId,
        deepLinkMode,
    );

    if (isWaitingForDeepLinkOrganization) {
        return null;
    }

    return <Outlet />;
}
